import {
  useConnectivityStatus,
  useUiOfflineSignal,
} from '@affine/core/components/hooks/use-connectivity-status';
import {
  useCollectionsFeatureEnabled,
  useTagsFeatureEnabled,
} from '@affine/core/modules/streamlain';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { useLiveData, useServices } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { WorkspaceViewContextService } from '../../streamlain/copilot/workspace-view-context';
import { WorkbenchService } from '../../workbench/services/workbench';
import { SEARCH_DEGRADED_STATUS } from '../impls/docs-search-status';
import { AdvancedSearchService } from '../services/advanced-search';
import { CMDKQuickSearchService } from '../services/cmdk';
import { PeopleSearchService } from '../services/people-search';
import { QuickSearchService } from '../services/quick-search';
import type { QuickSearchItem } from '../types/item';
import { AdvancedSearchView } from './advanced-search-dialog';
import { CMDK } from './cmdk';
import { groupQuickSearchItems } from './group-items';
import { PersonSearchFilter } from './person-filter';
import { SearchModeHeader } from './search-mode-header';
import { SearchSurface } from './search-surface';
import type { SearchSurfaceMode } from './search-surface-state';
import { useIndexerSummary } from './use-indexer-summary';

export const QuickSearchContainer = () => {
  const {
    quickSearchService,
    cMDKQuickSearchService,
    advancedSearchService,
    workspaceService,
    peopleSearchService,
    workspaceViewContextService,
    workbenchService,
  } = useServices({
    QuickSearchService,
    CMDKQuickSearchService,
    AdvancedSearchService,
    WorkspaceService,
    PeopleSearchService,
    WorkspaceViewContextService,
    WorkbenchService,
  });
  const quickSearch = quickSearchService.quickSearch;
  const open = useLiveData(quickSearch.show$);
  const query = useLiveData(quickSearch.query$);
  const loading = useLiveData(quickSearch.isLoading$);
  const loadingProgress = useLiveData(quickSearch.loadingProgress$);
  const items = useLiveData(quickSearch.items$);
  const error = useLiveData(quickSearch.error$);
  const status = useLiveData(quickSearch.status$);
  const footer = useLiveData(quickSearch.footer$);
  const desktopHint = useLiveData(quickSearch.desktopHint$);
  const options = useLiveData(quickSearch.options$);
  const advancedSearchOpen = useLiveData(advancedSearchService.open$);
  const collectionsEnabled = useCollectionsFeatureEnabled();
  const tagsEnabled = useTagsFeatureEnabled();
  const connectivityStatus = useConnectivityStatus();
  const hasQuery = query.trim().length > 0;
  const connectivitySearchDegraded = useUiOfflineSignal(
    open &&
      hasQuery &&
      workspaceService.workspace.flavour !== 'local' &&
      connectivityStatus.offline
  );
  const effectiveStatus =
    status ?? (connectivitySearchDegraded ? SEARCH_DEGRADED_STATUS : null);
  const i18n = useI18n();
  const searchTitle = i18n['Quick search']();

  const [searchRoot, setSearchRoot] = useState<HTMLDivElement | null>(null);
  const activeView = useLiveData(workbenchService.workbench.activeView$);
  const scope = useMemo(
    () => ({
      workspaceId: workspaceService.workspace.id,
      viewId: activeView.id,
    }),
    [workspaceService.workspace.id, activeView.id]
  );
  useEffect(() => {
    if (!searchRoot || !open) return;
    // The dialog only names itself; what it shows is read like any open popup.
    const read = () => {
      const location = activeView.location$.value;
      return {
        surfaceId: 'quicksearch',
        route: location.pathname + location.search + location.hash,
        title: searchTitle,
        summary: '',
        refs: [],
        elements: [],
        interactions: [],
        actions: [],
      };
    };
    return workspaceViewContextService.register(scope, 'quicksearch', read, {
      kind: 'dialog',
      isActive: () => searchRoot.isConnected && quickSearch.show$.value,
    });
  }, [
    searchRoot,
    open,
    scope,
    activeView,
    workspaceViewContextService,
    quickSearch,
    searchTitle,
  ]);
  const [advancedReady, setAdvancedReady] = useState(false);
  const surfaceReady = useMemo(
    () => ({
      quick: !loading || items.some(item => item.source === 'people'),
      advanced: advancedReady,
    }),
    [advancedReady, loading, items]
  );

  const handleClose = useCallback(() => {
    cMDKQuickSearchService.close();
  }, [cMDKQuickSearchService]);

  const selectedPerson = useLiveData(quickSearchService.personFilter$);
  const people = useLiveData(peopleSearchService.people$);
  const groups = useMemo(() => {
    const person =
      options?.allowAdvanced && selectedPerson
        ? people.find(person => person.id === selectedPerson.id)
        : null;
    const filtered =
      options?.allowAdvanced && selectedPerson
        ? items.filter(item => {
            const docId = (item.payload as { docId?: string })?.docId;
            return docId && person?.docIds.includes(docId);
          })
        : items;
    return groupQuickSearchItems(filtered);
  }, [items, options?.allowAdvanced, selectedPerson, people]);

  const handleChangeQuery = useCallback(
    (query: string) => {
      quickSearch.setQuery(query);
    },
    [quickSearch]
  );

  const handleSubmit = useCallback(
    (item: QuickSearchItem) => {
      quickSearch.submit(item);
    },
    [quickSearch]
  );

  const openAdvanced = useCallback(
    (initialQuery: string) => {
      cMDKQuickSearchService.openAdvanced(initialQuery, {
        collectionsEnabled,
        tagsEnabled,
      });
    },
    [cMDKQuickSearchService, collectionsEnabled, tagsEnabled]
  );

  const handleOpenAdvancedSearch = useCallback(() => {
    advancedSearchService.setPreferredView('advanced');
    openAdvanced(query);
  }, [advancedSearchService, openAdvanced, query]);

  const handleBackToNormal = useCallback(
    (currentQuery: string) => {
      advancedSearchService.setPreferredView('normal');
      advancedSearchService.close();
      quickSearch.setQuery(currentQuery);
      // Reuse the current result sessions when returning to the compact view.
      if (!quickSearch.show$.value)
        cMDKQuickSearchService.openCompact(currentQuery, {
          collectionsEnabled,
          tagsEnabled,
        });
    },
    [
      advancedSearchService,
      cMDKQuickSearchService,
      collectionsEnabled,
      tagsEnabled,
      quickSearch,
    ]
  );

  const handleModeChange = useCallback(
    (mode: SearchSurfaceMode) => {
      if (mode === 'advanced') handleOpenAdvancedSearch();
      else handleBackToNormal(query);
    },
    [handleBackToNormal, handleOpenAdvancedSearch, query]
  );
  const indexerSummary = useIndexerSummary();
  const allowAdvanced = advancedSearchOpen || !!options?.allowAdvanced;

  return (
    <SearchSurface
      contentRef={setSearchRoot}
      workspaceViewId={scope.viewId}
      workspaceId={scope.workspaceId}
      mode={advancedSearchOpen ? 'advanced' : open ? 'quick' : null}
      ready={surfaceReady}
      title={searchTitle}
      header={
        allowAdvanced ? (
          <SearchModeHeader
            mode={advancedSearchOpen ? 'advanced' : 'quick'}
            onModeChange={handleModeChange}
            status={indexerSummary}
          />
        ) : undefined
      }
      onClose={handleClose}
      quick={
        <CMDK
          navigationScope={advancedSearchOpen ? undefined : scope}
          navigationEnabled={open && !advancedSearchOpen}
          filters={options?.allowAdvanced ? <PersonSearchFilter /> : undefined}
          query={query}
          groups={groups}
          error={error ? UserFriendlyError.fromAny(error).message : null}
          status={!footer && effectiveStatus ? i18n.t(effectiveStatus) : null}
          desktopHint={desktopHint}
          footer={
            footer
              ? {
                  label: i18n.t(footer.label),
                  onClick: footer.action,
                  busy: footer.busy,
                }
              : null
          }
          loading={loading}
          loadingProgress={loadingProgress}
          onQueryChange={handleChangeQuery}
          onSubmit={handleSubmit}
          inputLabel={options?.label && i18n.t(options.label)}
          placeholder={options?.placeholder && i18n.t(options.placeholder)}
        />
      }
      advanced={
        <AdvancedSearchView
          navigationScope={advancedSearchOpen ? scope : undefined}
          collectionsEnabled={collectionsEnabled}
          tagsEnabled={tagsEnabled}
          onClose={handleClose}
          onReadyChange={setAdvancedReady}
        />
      }
    />
  );
};
